import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { TOTAL_REVIEWS_TEXT } from '../constants/reviews';
import { Menu, X, Phone, Calendar, Star, Instagram, Facebook, ChevronDown, MessageCircle, MapPin } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import T from '../i18n/T';
import PromoBar from './PromoBar';

const Header = () => {
  const { settings: siteSettings } = useSiteSettings();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [events, setEvents] = useState([]);
  const [showEventsDropdown, setShowEventsDropdown] = useState(false);
  const [menuItems, setMenuItems] = useState(() => {
    try {
      const cached = localStorage.getItem('headerMenu');
      if (cached) return JSON.parse(cached);
    } catch (e) { /* ignore */ }
    return null;
  });
  const [socialLinks, setSocialLinks] = useState({
    facebook: 'https://www.facebook.com/profile.php?id=61577946097571',
    instagram: 'https://www.instagram.com/nycroyalcarriage/'
  });
  const headerRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // ── The ONE runtime header measurement ─────────────────────────────────
  // Everything that has to clear the fixed header reads --header-height from
  // :root (Hero padding, anchor scroll-margin — see styles/fix-header-overlap.css).
  // The stylesheet only carries a no-JS / pre-hydration estimate; this effect
  // replaces it with the real bottom edge of the header block, including the
  // in-flow promo strip. Both share one fixed container so a hidden/removed
  // promo cannot leave a detached navigation row 44px down the viewport.
  // Subtract the open mobile menu; it must not shove the page around.
  // offsetHeight and the computed top ignore the headerSlideDown transform, so
  // the mount animation can't produce a bogus reading. Re-measured on header
  // resize (fonts, breakpoints), on the promo-active class toggle (the bar
  // resolves its coupon after mount), after the header's `top` transition, and
  // on window resize.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el || typeof window === 'undefined') return undefined;
    // react-snap runs this effect inside puppeteer (480px wide) and serialises
    // the DOM — an inline --header-height on <html> would then ship the phone
    // value to every visitor and beat the breakpoint defaults in the CSS. The
    // snapshot keeps the stylesheet estimate; real browsers measure.
    if (typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent || '')) return undefined;
    const root = document.documentElement;
    // A stale value serialised by an older build must not survive hydration.
    root.style.removeProperty('--header-height');
    let last = null;
    const measure = () => {
      const top = parseFloat(window.getComputedStyle(el).top) || 0;
      const menu = mobileMenuRef.current ? mobileMenuRef.current.offsetHeight : 0;
      const px = Math.round(top + el.offsetHeight - menu);
      if (px > 0 && px !== last) {
        last = px;
        root.style.setProperty('--header-height', `${px}px`);
      }
    };
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    const mo = typeof MutationObserver === 'function' ? new MutationObserver(measure) : null;
    if (mo) mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    el.addEventListener('transitionend', measure);
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      el.removeEventListener('transitionend', measure);
      window.removeEventListener('resize', measure);
      root.style.removeProperty('--header-height');
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      // Lock scroll without position:fixed (which causes CLS in CrUX data)
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      document.body.style.overscrollBehavior = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.events, { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          // API may return either a raw array or { events: [...] }
          const eventsList = Array.isArray(data) ? data : (data.events || []);
          // Only show active events in dropdown
          const activeEvents = eventsList.filter(e => e.is_active !== 0 && e.is_active !== false);
          setEvents(activeEvents);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    const idle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(() => fetchEvents())
      : setTimeout(fetchEvents, 2000);
    return () => typeof cancelIdleCallback === 'function' ? cancelIdleCallback(idle) : clearTimeout(idle);
  }, []);

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about/' },
    { name: 'Tours & Prices', href: '/tours/' },
    { name: '💍 Proposal', href: '/proposals/', proposal: true },
    // 🎄 Top-level Christmas Tour entry — wanted in the menu even before the
    // season opens (Nov 21). The emoji + `christmas` flag let the renderer
    // visually highlight it (different colour/weight) without restyling the
    // rest of the nav. Once Christmas is over we can remove this entry or
    // swap it for a Valentine's link in February.
    { name: '🎄 Christmas Tour', href: '/events/christmas/', christmas: true },
    { name: 'Our Horses', href: '/horses/' },
    { name: 'Tour Guide', href: '/tour-guide/' },
    { name: 'Gallery', href: '/gallery/' },
    { name: 'Blog', href: '/blog/' },
    { name: 'FAQ', href: '/faq/' },
    { name: 'Contact', href: '/contact/' }
  ];

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        // Cache-bust to bypass Cloudflare edge cache for menu changes
        const response = await fetch('/api/menu', { cache: 'no-store' });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            const items = data
              .filter(item => item.is_active === 1)
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
              .map(item => ({
                name: item.label,
                href: item.url,
                scrollId: '',
              }));
            if (items.length > 0) {
              // Always update state and cache with fresh data from API
              // (cached menu items show instantly; API updates replace them)
              setMenuItems(items);
              try { localStorage.setItem('headerMenu', JSON.stringify(items)); } catch (e) { /* ignore */ }
            }
          }
        }
      } catch (error) { /* fallback to navItems */ }
    };
    // Always fetch menu from API to catch admin changes (Christmas toggle etc.)
    // Cached localStorage menu is used as initial state for fast render,
    // then API response replaces it if different.
    const idle = typeof requestIdleCallback === 'function'
      ? requestIdleCallback(() => fetchMenu())
      : setTimeout(fetchMenu, 2500);
    return () => typeof cancelIdleCallback === 'function' ? cancelIdleCallback(idle) : clearTimeout(idle);
  }, []);

  // DB items override hardcoded navItems. We then:
  //   1. Force a 💍 prefix onto any Proposal entry (DB-managed menus may not
  //      carry the emoji; the owner wants it visible in every nav surface).
  //   2. Force-inject the Christmas Tour link if neither source includes it —
  //      this way the Christmas entry is guaranteed visible regardless of how
  //      the admin has configured the menu in the DB. Once Christmas ends
  //      (Jan 9, 2027) this injection can be removed.
  const displayMenuItems = (() => {
    let base = menuItems || navItems;

    // 1) Add 💍 to Proposal everywhere (header + mobile + DB-driven menus).
    base = base.map(it => {
      const isProposal = it.proposal === true
        || it.href === '/proposals/' || it.href === '/proposals'
        || it.href === '/proposal/'  || it.href === '/proposal';
      if (isProposal && it.name && !/💍/.test(it.name)) {
        return { ...it, name: `💍 ${it.name}`, proposal: true };
      }
      return it;
    });

    // 2) Inject 🎄 Christmas Tour if missing.
    const hasChristmas = base.some(it => it.href === '/events/christmas/' || it.href === '/events/christmas');
    if (hasChristmas) return base;
    const christmasItem = { name: '🎄 Christmas Tour', href: '/events/christmas/', christmas: true };
    // Insert right after Proposal so the seasonal entry sits with the other
    // headline experiences (Tours, Proposal, Christmas) rather than at the end.
    const idx = base.findIndex(it => /proposal/i.test(it.name || ''));
    if (idx >= 0) {
      const next = [...base];
      next.splice(idx + 1, 0, christmasItem);
      return next;
    }
    return [...base, christmasItem];
  })();

  // Read social links from shared SiteSettingsContext (no extra fetch)
  useEffect(() => {
    if (siteSettings.social_facebook || siteSettings.social_instagram) {
      setSocialLinks({
        facebook: siteSettings.social_facebook || 'https://www.facebook.com/profile.php?id=61577946097571',
        instagram: siteSettings.social_instagram || 'https://www.instagram.com/nycroyalcarriage/'
      });
    }
  }, [siteSettings]);

  const location = useLocation();


  return (
    <>
    <header
      ref={headerRef}
      style={{ animation: 'headerSlideDown 0.5s cubic-bezier(0.25, 1, 0.5, 1) both' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-sm' : 'bg-white'
      }`}
    >
      <PromoBar />
      <a href="#main-content" className="skip-to-content"><T>Skip to content</T></a>
      {/* Top Bar */}
      <div className="hidden md:block py-2 border-b border-brand-200/40 bg-white">
        <div className="container-custom">
          <div className="flex items-center justify-between text-sm">
            <span className="text-neutral-600 tracking-wide flex items-center">
              <MapPin className="w-3 h-3 text-brand-700 mr-1 flex-shrink-0" />
              <span className="text-neutral-600 text-xs">7th Ave & Central Park S, New York, NY 10019</span>
              <span className="mx-2.5 text-neutral-300">|</span>
              <span className="text-neutral-600">Since 1999</span>
            </span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-neutral-600">
                <Star className="w-3.5 h-3.5 fill-brand-400 text-brand-400" />
                <span className="text-xs font-medium">{TOTAL_REVIEWS_TEXT}</span>
              </div>
              <div className="h-3.5 w-px bg-neutral-300"></div>
              <div className="flex items-center gap-2.5">
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-neutral-600 hover:text-brand-700 transition-colors"><Instagram className="w-3.5 h-3.5" aria-hidden="true" /></a>
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-neutral-600 hover:text-brand-700 transition-colors"><Facebook className="w-3.5 h-3.5" aria-hidden="true" /></a>
                <a href="https://wa.me/16466569717" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-neutral-600 hover:text-sage-500 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                </a>
              </div>
              <div className="h-3.5 w-px bg-neutral-300"></div>
              <a href="tel:+16466569717" className="flex items-center gap-1.5 text-neutral-700 hover:text-brand-700 font-semibold transition-colors">
                <Phone className="w-3.5 h-3.5" /><span dir="ltr">(646) 656-9717</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container-custom">
        <div className="py-3 md:py-4">
          <div className="flex items-center justify-between md:mb-4">
            <Link to="/">
              <div className="flex items-center gap-3 transition-transform duration-200 hover:scale-[1.02]">
                <div className="w-10 h-10 md:w-14 md:h-14 rounded-full overflow-hidden shadow-sm">
                  <img src="/images/nycroyallogo-112.webp" alt="NYC Royal Carriage Logo" width="56" height="56" className="w-full h-full object-cover" />
                </div>
                <div>
                  <span className="text-lg md:text-xl font-display font-semibold text-neutral-900 tracking-tight">NYC Royal Carriage</span>
                  <p className="hidden md:block text-xs font-medium text-brand-700 tracking-[0.15em] uppercase">Est. 1999</p>
                </div>
              </div>
            </Link>

            <div className="flex items-center gap-2">
              {/* Mobile-only quick-call button — vivid emerald green so it
                  reads as a universal "call" affordance (the previous sage
                  was too muted; owner asked for clearly green). Matches the
                  bottom CTA bar's Call Us button for a consistent visual
                  language across all phone-call entry points. */}
              <a href="tel:+16466569717" aria-label="Call us" className="md:hidden bg-emerald-700 hover:bg-emerald-800 active:bg-emerald-800 text-white p-2.5 rounded-lg transition-colors shadow-sm">
                <Phone className="w-4 h-4" aria-hidden="true" strokeWidth={2.5} fill="currentColor" />
              </a>
              {/* Language switcher — persists choice in localStorage, drives the
                  useI18n() context throughout the site. */}
              <LanguageSwitcher compact />
              <div className="hidden lg:flex">
                <Link
                  to="/book"
                  className="bg-neutral-900 hover:bg-neutral-800 text-neutral-50 px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 tracking-wide"
                >
                  <Calendar className="w-4 h-4" /><span><T>RESERVE NOW</T></span>
                </Link>
              </div>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMenuOpen} className="xl:hidden p-2 text-neutral-700 hover:text-brand-600 transition-colors">
                {isMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
              </button>
            </div>
          </div>

          <nav className="hidden xl:block" aria-label="Main navigation">
            <div className="flex items-center gap-8">
              {displayMenuItems.map((item) => {
                // Highlight the Christmas Tour entry so it pops in the menu —
                // detect by the marker flag we set OR by href pattern (covers
                // both the hardcoded navItems and any DB-managed menu).
                const isChristmasItem = item.christmas === true || item.href === '/events/christmas/' || item.href === '/events/christmas';
                return (
                  <Link
                    key={item.name || item.id}
                    to={item.href}
                    onClick={(e) => {
                      if (location.pathname === item.href) {
                        const scrollId = item.scroll_id || item.scrollId;
                        if (scrollId) {
                          e.preventDefault();
                          const element = document.querySelector(`#${scrollId}`);
                          if (element) {
                            // Land just below the measured header instead of a fixed 120px guess.
                            const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 80;
                            window.scrollTo({ top: element.offsetTop - headerHeight - 16, behavior: 'smooth' });
                          }
                        }
                      }
                    }}
                    className={
                      isChristmasItem
                        ? 'text-accent-700 hover:text-accent-800 font-semibold transition-colors text-sm relative group'
                        : 'text-neutral-600 hover:text-neutral-900 font-medium transition-colors text-sm relative group'
                    }
                  >
                    <T emoji>{item.name}</T>
                    <span className={
                      isChristmasItem
                        ? 'absolute -bottom-1 left-0 h-px bg-accent-700 transition-all duration-300 w-full'
                        : 'absolute -bottom-1 left-0 h-px bg-brand-500 transition-all duration-300 w-0 group-hover:w-full'
                    }></span>
                  </Link>
                );
              })}
              {(siteSettings?.events_nav_visible === true || siteSettings?.events_nav_visible === 'true') && events.length > 0 && (
                <div className="relative" onMouseEnter={() => setShowEventsDropdown(true)} onMouseLeave={() => setShowEventsDropdown(false)}>
                  <button className="text-neutral-600 hover:text-neutral-900 font-medium transition-colors text-sm relative group flex items-center gap-1">
                    <span>Events</span>
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showEventsDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showEventsDropdown && (
                    <div
                      className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 py-2 z-50"
                      style={{ animation: 'dropdownFadeIn 0.2s cubic-bezier(0.25, 1, 0.5, 1) both' }}
                    >
                      {events.map((event) => (
                        <a key={event.id} href={event.url || '#'} target={event.url ? '_blank' : '_self'} rel={event.url ? 'noopener noreferrer' : ''}
                          className="block px-4 py-3 text-sm text-neutral-700 hover:bg-brand-50 hover:text-brand-700 transition-colors border-b border-neutral-100 last:border-b-0">
                          <div className="font-medium">{event.name}</div>
                          {event.description && <div className="text-xs text-neutral-600 mt-1 line-clamp-2">{event.description}</div>}
                          {(event.start_date || event.end_date) && (
                            <div className="text-xs text-brand-700 mt-1">
                              {event.start_date && new Date(event.start_date).toLocaleDateString('en-US')}
                              {event.start_date && event.end_date && ' - '}
                              {event.end_date && new Date(event.end_date).toLocaleDateString('en-US')}
                            </div>
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="xl:hidden bg-neutral-50/98 backdrop-blur-md border-t border-neutral-200 overflow-hidden"
          style={{ maxWidth: '100vw', animation: 'mobileMenuSlideDown 0.3s cubic-bezier(0.25, 1, 0.5, 1) both' }}
        >
          <div className="container-custom py-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 100px)' }}>
            <nav className="space-y-1">
              {displayMenuItems.map((item) => {
                const isChristmasItem = item.christmas === true || item.href === '/events/christmas/' || item.href === '/events/christmas';
                return (
                  <Link key={item.name || item.id} to={item.href} onClick={() => setIsMenuOpen(false)}
                    className={
                      isChristmasItem
                        ? 'block w-full text-left text-accent-700 hover:text-accent-800 hover:bg-accent-50 font-semibold py-3 px-3 rounded-lg transition-colors'
                        : 'block w-full text-left text-neutral-700 hover:text-neutral-900 hover:bg-brand-50 font-medium py-3 px-3 rounded-lg transition-colors'
                    }>
                    <T emoji>{item.name}</T>
                  </Link>
                );
              })}
              {(siteSettings?.events_nav_visible === true || siteSettings?.events_nav_visible === 'true') && events.length > 0 && (
                <div className="pt-3 mt-3 border-t border-neutral-200">
                  <div className="text-xs font-semibold text-neutral-600 mb-2 px-3 uppercase tracking-wider">Events</div>
                  {events.map((event) => (
                    <a key={event.id} href={event.url || '#'} target={event.url ? '_blank' : '_self'} rel={event.url ? 'noopener noreferrer' : ''}
                      className="block text-neutral-600 hover:text-brand-700 py-2.5 px-3 rounded-lg hover:bg-brand-50 transition-colors">
                      <div className="font-medium text-sm">{event.name}</div>
                    </a>
                  ))}
                </div>
              )}
              <div className="pt-6 mt-3 border-t border-neutral-200 space-y-4">
                <a href="tel:+16466569717" className="flex items-center gap-2 text-brand-700 font-medium px-3">
                  <Phone className="w-4 h-4" /><span dir="ltr">(646) 656-9717</span>
                </a>
                <div className="flex flex-wrap items-center gap-4 px-3">
                  <a href="https://wa.me/16466569717" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sage-600 hover:text-sage-700 text-sm">
                    <MessageCircle className="w-4 h-4" /><span>WhatsApp</span>
                  </a>
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-neutral-600 hover:text-brand-600 text-sm">
                    <Instagram className="w-4 h-4" /><span>Instagram</span>
                  </a>
                </div>
                <Link to="/book" onClick={() => setIsMenuOpen(false)}
                  className="bg-neutral-900 hover:bg-neutral-800 text-neutral-50 px-6 py-3 rounded-lg font-semibold w-full flex items-center justify-center gap-2 transition-colors">
                  <Calendar className="w-4 h-4" /><span><T>RESERVE NOW</T></span>
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
    </>
  );
};

export default Header;
